import { makeAutoObservable, runInAction } from 'mobx';
import services from '../services';

/**
 * Super-panel state for webhooks and their deliveries.
 *
 * Both lists are server-paged and filtered, so each keeps a query object that
 * the table's paging/filter callbacks mutate. `loadEvents` backs three places -
 * the global events page, the events table on a webhook's detail page, and the
 * refresh after a manual action - by taking the filter overrides as an
 * argument rather than each caller owning its own loader.
 */
class WebhooksStore {
    constructor() {
        makeAutoObservable(this);
    }

    // --- webhooks ---------------------------------------------------------
    webhooks = [];
    webhooksCount = 0;
    loadingWebhooks = false;
    webhookQuery = {
        p: 0,
        n: 25,
        id: '',
        search: '',
        status: '',
        scope: '',
        companyId: '',
        partnerId: '',
    };

    webhook = null;
    loadingWebhook = false;

    // --- events -----------------------------------------------------------
    events = [];
    eventsCount = 0;
    loadingEvents = false;
    eventQuery = {
        p: 0,
        n: 25,
        webhookId: '',
        eventName: '',
        deliveryStatus: '',
        isTest: '',
    };

    event = null;
    loadingEvent = false;
    runningEvent = false;
    reschedulingEvent = false;

    error = null;

    setError(error) {
        this.error = error;
    }

    /** Strip empty filters so they never reach the API as `?status=`. */
    _clean(query) {
        return Object.fromEntries(
            Object.entries(query).filter(
                ([, value]) => value !== '' && value !== null && value !== undefined
            )
        );
    }

    async loadWebhooks(overrides = {}) {
        runInAction(() => {
            this.webhookQuery = { ...this.webhookQuery, ...overrides };
            this.loadingWebhooks = true;
            this.error = null;
        });
        try {
            const result = await services.Webhooks.list(
                this._clean(this.webhookQuery)
            );
            runInAction(() => {
                this.webhooks = result.rows || [];
                this.webhooksCount = result.count || 0;
            });
        } catch (e) {
            runInAction(() => (this.error = e));
        } finally {
            runInAction(() => (this.loadingWebhooks = false));
        }
    }

    async loadWebhook(webhookId) {
        runInAction(() => {
            this.loadingWebhook = true;
            this.error = null;
        });
        try {
            const webhook = await services.Webhooks.fetch(webhookId);
            runInAction(() => (this.webhook = webhook));
        } catch (e) {
            runInAction(() => (this.error = e));
        } finally {
            runInAction(() => (this.loadingWebhook = false));
        }
    }

    async loadEvents(overrides = {}) {
        runInAction(() => {
            this.eventQuery = { ...this.eventQuery, ...overrides };
            this.loadingEvents = true;
            this.error = null;
        });
        try {
            const result = await services.WebhookEvents.list(
                this._clean(this.eventQuery)
            );
            runInAction(() => {
                this.events = result.rows || [];
                this.eventsCount = result.count || 0;
            });
        } catch (e) {
            runInAction(() => (this.error = e));
        } finally {
            runInAction(() => (this.loadingEvents = false));
        }
    }

    /** Reset paging/filters when moving between the two event tables. */
    resetEventQuery(overrides = {}) {
        runInAction(() => {
            this.eventQuery = {
                p: 0,
                n: 25,
                webhookId: '',
                eventName: '',
                deliveryStatus: '',
                isTest: '',
                ...overrides,
            };
            this.events = [];
            this.eventsCount = 0;
        });
    }

    async loadEvent(eventId) {
        runInAction(() => {
            this.loadingEvent = true;
            this.error = null;
        });
        try {
            const event = await services.WebhookEvents.fetch(eventId);
            runInAction(() => (this.event = event));
        } catch (e) {
            runInAction(() => (this.error = e));
        } finally {
            runInAction(() => (this.loadingEvent = false));
        }
    }

    // Both actions answer with the refreshed event, so the detail view updates
    // from the same response that performed the change.
    async runEventNow(eventId) {
        runInAction(() => {
            this.runningEvent = true;
            this.error = null;
        });
        try {
            const event = await services.WebhookEvents.runNow(eventId);
            runInAction(() => (this.event = event));
            return event;
        } catch (e) {
            runInAction(() => (this.error = e));
        } finally {
            runInAction(() => (this.runningEvent = false));
        }
    }

    async rescheduleEvent(eventId, nextTry) {
        runInAction(() => {
            this.reschedulingEvent = true;
            this.error = null;
        });
        try {
            const event = await services.WebhookEvents.reschedule(
                eventId,
                nextTry
            );
            runInAction(() => (this.event = event));
            return event;
        } catch (e) {
            runInAction(() => (this.error = e));
        } finally {
            runInAction(() => (this.reschedulingEvent = false));
        }
    }
}

export default WebhooksStore;
